import { Injectable } from '@angular/core';
import { Program, ProgramItem } from '@app/app/page-modules/programs/models/program.model';
import { Page } from '@app/app/shared/models/page';
import { ObservableResult } from '@app/app/shared/store';
import { delay } from 'rxjs/operators';
import {
  CreateProgramFromTemplateRequest,
  CreateProgramResponse,
  ProgramCreationRequest,
  ProgramDataService,
  ProgramSectionBulkUpdateItem,
  ProgramSectionRequestUnion,
  ProgramSectionResponse,
  ProgramUpdateRequest,
  UpdateProgramResponse,
} from './data.service';

@Injectable()
export class MockProgramDataService implements ProgramDataService {
  private programs: Program[] = [
    {
      _id: '1',
      title: 'Software Engineering Programme',
      uri: 'software-engineering',
      formattedUri: 'software-engineering',
      header: 'Software Engineering Programme',
      subHeader: 'A comprehensive software engineering programme covering full-stack development.',
      mainSection: {
        cards: [],
        cardsLayoutType: 'CardView',
        uid: 'main-1',
        title: 'Software Engineering Programme',
        content: [
          {
            type: 'PARAGRAPH',
            content: 'Hello',
          },
        ],
        layoutType: 'TOP_BANNER',
      },
      standardSections: [
        {
          cards: [],
          cardsLayoutType: 'CardView',
          uid: 'section-1',
          title: 'Core Modules',
          content: [
            {
              type: 'PARAGRAPH',
              content: 'Hello',
            },
          ],
          layoutType: 'NO_BANNER',
        },
      ],
      authorDetails: {
        uid: 'author-1',
        name: 'Tech Academy',
        organization: 'Tech Institute',
        displayConfig: {
          authorName: true,
          organizationName: true,
        },
        orgLogoUrl: '',
        userImageUrl: '',
      },
      permission: {
        canEdit: false,
        canPublish: false,
        canViewAnalytics: false,
        canDoMemberExports: false,
      },
      lastUpdatedOn: '2023-12-01T10:30:00Z',
      status: 'PUBLISHED',
      tags: [
        { title: 'Programming', _id: 'tag-1' },
        { title: 'Software Engineering', _id: 'tag-2' },
      ],
      categories: [{ title: 'Technology', frameworkId: 'tech-framework' }],
      completed: false,
      completedOn: null,
      likes: {
        totalLikes: 45,
        vote: false,
      },
      favorite: false,
      type: 'INSTITUTIONAL_LIBRARY',
      timeRequired: 180,
      availableLanguages: [{ code: 'en', name: 'English', enabled: true }],
      adminRights: [],
      embedded: false,
      completedCardsCount: 0,
      totalCardsCount: 15,
      dataForms: [],
      userPlaylistSubmissionSummary: undefined,
      draft: undefined,
    },
    {
      _id: '2',
      title: 'Data Science Programme',
      uri: 'data-science',
      formattedUri: 'data-science',
      header: 'Data Science Programme',
      subHeader: 'A comprehensive data science programme covering machine learning and AI.',
      mainSection: {
        cards: [],
        cardsLayoutType: 'CardView',
        uid: 'main-2',
        title: 'Data Science Programme',
        content: [
          {
            type: 'PARAGRAPH',
            content: 'Data Science Programme Content',
          },
        ],
        layoutType: 'TOP_BANNER',
      },
      standardSections: [],
      authorDetails: {
        uid: 'author-2',
        name: 'Data Academy',
        organization: 'Data Institute',
        displayConfig: {
          authorName: true,
          organizationName: true,
        },
        orgLogoUrl: '',
        userImageUrl: '',
      },
      permission: {
        canEdit: false,
        canPublish: false,
        canViewAnalytics: false,
        canDoMemberExports: false,
      },
      lastUpdatedOn: '2023-12-01T10:30:00Z',
      status: 'PUBLISHED',
      tags: [
        { title: 'Data Science', _id: 'tag-3' },
        { title: 'Machine Learning', _id: 'tag-4' },
      ],
      categories: [{ title: 'Technology', frameworkId: 'tech-framework' }],
      completed: false,
      completedOn: null,
      likes: {
        totalLikes: 32,
        vote: false,
      },
      favorite: false,
      type: 'INSTITUTIONAL_LIBRARY',
      timeRequired: 240,
      availableLanguages: [{ code: 'en', name: 'English', enabled: true }],
      adminRights: [],
      embedded: false,
      completedCardsCount: 0,
      totalCardsCount: 20,
      dataForms: [],
      userPlaylistSubmissionSummary: undefined,
      draft: undefined,
    },
    {
      _id: '3',
      title: 'Web Development Programme',
      uri: 'web-development',
      formattedUri: 'web-development',
      header: 'Web Development Programme',
      subHeader: 'A modern web development programme covering React, Angular and Vue.',
      mainSection: {
        cards: [],
        cardsLayoutType: 'CardView',
        uid: 'main-3',
        title: 'Web Development Programme',
        content: [
          {
            type: 'PARAGRAPH',
            content: 'Web Development Programme Content',
          },
        ],
        layoutType: 'TOP_BANNER',
      },
      standardSections: [],
      authorDetails: {
        uid: 'author-3',
        name: 'Web Academy',
        organization: 'Web Institute',
        displayConfig: {
          authorName: true,
          organizationName: true,
        },
        orgLogoUrl: '',
        userImageUrl: '',
      },
      permission: {
        canEdit: false,
        canPublish: false,
        canViewAnalytics: false,
        canDoMemberExports: false,
      },
      lastUpdatedOn: '2023-12-01T10:30:00Z',
      status: 'PUBLISHED',
      tags: [
        { title: 'Web Development', _id: 'tag-5' },
        { title: 'Frontend', _id: 'tag-6' },
      ],
      categories: [{ title: 'Technology', frameworkId: 'tech-framework' }],
      completed: false,
      completedOn: null,
      likes: {
        totalLikes: 28,
        vote: false,
      },
      favorite: false,
      type: 'INSTITUTIONAL_LIBRARY',
      timeRequired: 160,
      availableLanguages: [{ code: 'en', name: 'English', enabled: true }],
      adminRights: [],
      embedded: false,
      completedCardsCount: 0,
      totalCardsCount: 12,
      dataForms: [],
      userPlaylistSubmissionSummary: undefined,
      draft: undefined,
    },
  ];

  getPrograms(): ObservableResult<Page<ProgramItem>> {
    const page: Page<ProgramItem> = {
      content: [],
      currentPageNumber: 0,
      hasContent: true,
      pageSize: 10,
      numberOfPages: 0,
      totalNumberOfElement: 2,
    };
    return ObservableResult.ofSuccess(page).pipe(delay(300));
  }

  getProgram(programUri: string, languageCode?: string, playlistPath?: string): ObservableResult<Program> {
    const program = this.programs.find((p) => p.uri === programUri);
    if (!program) {
      throw new Error(`Programme with uri ${programUri} not found`);
    }
    return ObservableResult.ofSuccess(program).pipe(delay(300));
  }

  createProgram(request: ProgramCreationRequest): ObservableResult<CreateProgramResponse> {
    const newProgram: Program = {
      _id: `program-${Date.now()}`,
      title: request.mainSection.title || 'Untitled Programme',
      uri: `program-${Date.now()}`,
      formattedUri: `program-${Date.now()}`,
      header: request.mainSection.title || 'Untitled Programme',
      subHeader: '',
      mainSection: {
        cards: [],
        cardsLayoutType: 'CardView',
        uid: `main-${Date.now()}`,
        title: request.mainSection.title || '',
        content: request.mainSection.content || [],
        layoutType: request.mainSection.layoutType,
      },
      standardSections: [],
      authorDetails: {
        uid: 'mock-author',
        name: 'Mock Author',
        organization: 'Mock Organization',
        displayConfig: { authorName: true, organizationName: true },
        orgLogoUrl: '',
        userImageUrl: '',
      },
      permission: {
        canEdit: true,
        canPublish: true,
        canViewAnalytics: false,
        canDoMemberExports: false,
      },
      lastUpdatedOn: new Date().toISOString(),
      status: 'NEW',
      tags: [],
      categories: [],
      completed: false,
      completedOn: null,
      likes: { totalLikes: 0, vote: false },
      favorite: false,
      type: 'INSTITUTIONAL_LIBRARY',
      timeRequired: 0,
      availableLanguages: [{ code: 'en', name: 'English', enabled: true }],
      adminRights: [],
      embedded: false,
      completedCardsCount: 0,
      totalCardsCount: 0,
      dataForms: [],
      userPlaylistSubmissionSummary: undefined,
      draft: undefined,
    };

    this.programs.push(newProgram);

    return ObservableResult.ofSuccess({
      item: newProgram,
      uploadUrl: null,
    } as CreateProgramResponse).pipe(delay(300));
  }

  createProgramFromTemplate(request: CreateProgramFromTemplateRequest): ObservableResult<CreateProgramResponse> {
    const newProgram: Program = {
      _id: `program-from-template-${Date.now()}`,
      title: `Programme from template ${request.templateUid}`,
      uri: `program-from-template-${Date.now()}`,
      formattedUri: `program-from-template-${Date.now()}`,
      header: `Programme from template ${request.templateUid}`,
      subHeader: '',
      mainSection: {
        cards: [],
        cardsLayoutType: 'CardView',
        uid: `main-${Date.now()}`,
        title: `Programme from template ${request.templateUid}`,
        content: [],
        layoutType: 'TOP_BANNER',
      },
      standardSections: [],
      authorDetails: {
        uid: 'mock-author',
        name: 'Mock Author',
        organization: 'Mock Organization',
        displayConfig: { authorName: true, organizationName: true },
        orgLogoUrl: '',
        userImageUrl: '',
      },
      permission: {
        canEdit: true,
        canPublish: true,
        canViewAnalytics: false,
        canDoMemberExports: false,
      },
      lastUpdatedOn: new Date().toISOString(),
      status: 'NEW',
      tags: [],
      categories: [],
      completed: false,
      completedOn: null,
      likes: { totalLikes: 0, vote: false },
      favorite: false,
      type: 'INSTITUTIONAL_LIBRARY',
      timeRequired: 0,
      availableLanguages: [{ code: 'en', name: 'English', enabled: true }],
      adminRights: [],
      embedded: false,
      completedCardsCount: 0,
      totalCardsCount: 0,
      dataForms: [],
      userPlaylistSubmissionSummary: undefined,
      draft: undefined,
    };

    this.programs.push(newProgram);

    return ObservableResult.ofSuccess({
      item: newProgram,
      uploadUrl: null,
    } as CreateProgramResponse).pipe(delay(300));
  }

  updateProgram(programUid: string, request: ProgramUpdateRequest, languageCode?: string): ObservableResult<UpdateProgramResponse> {
    const program = this.programs.find((p) => p._id === programUid);
    if (program) {
      program.mainSection = {
        ...program.mainSection,
        title: request.mainSection.title,
        content: request.mainSection.content || [],
        layoutType: request.mainSection.layoutType,
      };
    }
    return ObservableResult.ofSuccess({
      uri: program?.uri || programUid,
      formattedUri: program?.formattedUri || programUid,
      coverImageUploadUrl: null,
    }).pipe(delay(300));
  }

  addProgramSection(
    programUid: string,
    request: ProgramSectionRequestUnion,
    languageCode?: string,
  ): ObservableResult<ProgramSectionResponse> {
    const response: ProgramSectionResponse = {
      uid: `section-${Date.now()}`,
    };
    return ObservableResult.ofSuccess(response).pipe(delay(300));
  }

  updateProgramSection(
    programUid: string,
    sectionUid: string,
    request: ProgramSectionRequestUnion,
    languageCode?: string,
  ): ObservableResult<ProgramSectionResponse> {
    return ObservableResult.ofSuccess({ uid: sectionUid, items: [] }).pipe(delay(300));
  }

  bulkUpdateProgramSections(programUid: string, sections: ProgramSectionBulkUpdateItem[]): ObservableResult<void> {
    return ObservableResult.ofSuccess().pipe(delay(300));
  }

  deleteProgramSection(programUid: string, sectionUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess().pipe(delay(300));
  }

  reorderProgramSections(programUid: string, request: { sections: string[] }): ObservableResult<void> {
    return ObservableResult.ofSuccess().pipe(delay(300));
  }

  publishProgram(programUid: string): ObservableResult<void> {
    const program = this.programs.find((p) => p._id === programUid);
    if (program) {
      program.status = 'PUBLISHED';
    }
    return ObservableResult.ofSuccess().pipe(delay(300));
  }

  unpublishProgram(programUid: string): ObservableResult<void> {
    const program = this.programs.find((p) => p._id === programUid);
    if (program) {
      program.status = 'UNPUBLISHED';
    }
    return ObservableResult.ofSuccess().pipe(delay(300));
  }

  archiveProgram(programUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess().pipe(delay(300));
  }
}
